<template>
  <div class="gf-dashboard">
    <!-- KPI Cards -->
    <v-row dense>
      <v-col cols="6" sm="6" md="3">
        <div class="kpi-card kpi-1">
          <div class="kpi-label">Kurse</div>
          <div class="kpi-value">
            <number :from="0" :to="totals.courseCount" :duration="0.8" />
          </div>
          <div class="kpi-sub">Im Zeitraum</div>
        </div>
      </v-col>
      <v-col cols="6" sm="6" md="3">
        <div class="kpi-card kpi-2">
          <div class="kpi-label">Anwesende TN</div>
          <div class="kpi-value">
            <number :from="0" :to="totals.totalVisited" :duration="0.8" />
          </div>
          <div class="kpi-sub">Ø {{ totals.avgVisited }} / Kurs</div>
        </div>
      </v-col>
      <v-col cols="6" sm="6" md="3">
        <div class="kpi-card kpi-3">
          <div class="kpi-label">Auslastung</div>
          <div class="kpi-value">{{ totals.utilization }}%</div>
          <div class="kpi-sub">Plätze belegt</div>
        </div>
      </v-col>
      <v-col cols="6" sm="6" md="3">
        <div class="kpi-card kpi-4">
          <div class="kpi-label">Ø Bewertung</div>
          <div class="kpi-value">{{ totals.avgRating || "–" }} ★</div>
          <div class="kpi-sub">{{ totals.ratingCount }} Bewertungen</div>
        </div>
      </v-col>
    </v-row>

    <!-- Tabs -->
    <v-card class="mt-4 glass-card" flat>
      <v-tabs v-model="activeTab" background-color="transparent" dark grow show-arrows>
        <v-tab>Übersicht</v-tab>
        <v-tab>Kurs-Vergleich</v-tab>
        <v-tab>Wochentag</v-tab>
        <v-tab>Heatmap</v-tab>
        <v-tab>Instruktoren</v-tab>
        <v-tab>Rohdaten</v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab" style="background: transparent">
        <!-- ============ TAB 1: ÜBERSICHT ============ -->
        <v-tab-item>
          <div class="pa-4">
            <div class="d-flex justify-end mb-3">
              <v-btn small color="primary" @click="exportOverview">
                <v-icon left small>mdi-download</v-icon>
                Excel
              </v-btn>
            </div>
            <v-row>
              <v-col cols="12" md="6">
                <div class="chart-card">
                  <div class="chart-title">Ø Teilnehmer pro Studio</div>
                  <apexchart type="bar" height="320" :options="studioAvgOptions" :series="studioAvgSeries" />
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="chart-card">
                  <div class="chart-title">Auslastung pro Studio (%)</div>
                  <apexchart type="bar" height="320" :options="studioUtilOptions" :series="studioUtilSeries" />
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="chart-card">
                  <div class="chart-title">Aktive GF-Mitglieder (% der Studio-Mitglieder)</div>
                  <apexchart type="bar" height="320" :options="activeMemberOptions" :series="activeMemberSeries" />
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="chart-card">
                  <div class="chart-title">Kurse pro Tag (Verlauf)</div>
                  <apexchart type="area" height="320" :options="trendOptions" :series="trendSeries" />
                </div>
              </v-col>
            </v-row>
          </div>
        </v-tab-item>

        <!-- ============ TAB 2: KURS-VERGLEICH (Kurs auswählen → alle Slots) ============ -->
        <v-tab-item>
          <div class="pa-4">
            <div class="d-flex flex-wrap align-center mb-3" style="gap: 12px">
              <v-autocomplete
                v-model="selectedCourseId"
                :items="courseOptions"
                item-text="name"
                item-value="id"
                label="Kurs auswählen"
                dark
                hide-details
                dense
                outlined
                style="max-width: 360px" />
              <v-spacer />
              <v-btn small color="primary" :disabled="!selectedCourseId" @click="exportCourseSlots">
                <v-icon left small>mdi-download</v-icon>
                Excel
              </v-btn>
            </div>

            <div v-if="!selectedCourseId" class="empty-state">Wähle einen Kurs, um alle Slots zu vergleichen.</div>

            <template v-else>
              <v-row>
                <v-col cols="12" md="7">
                  <div class="chart-card">
                    <div class="chart-title">Ø Teilnehmer pro Slot</div>
                    <apexchart type="bar" height="360" :options="courseSlotOptions" :series="courseSlotSeries" />
                  </div>
                </v-col>
                <v-col cols="12" md="5">
                  <div class="chart-card">
                    <div class="chart-title">Beste Slots (Top 5)</div>
                    <div v-for="(s, i) in topSlotsForCourse" @click="openDrilldown(s)" :key="s.key" class="top-slot-row">
                      <div class="rank">#{{ i + 1 }}</div>
                      <div class="flex-grow-1">
                        <div class="slot-title">{{ weekdayName(s.weekday) }} {{ formatTime(s.hour, s.minute) }}</div>
                        <div class="slot-sub">{{ s.studioName }}</div>
                      </div>
                      <div class="text-right">
                        <div class="slot-value">{{ s.avgVisited }}</div>
                        <div class="slot-sub">{{ s.executions }}× • {{ s.utilization }}%</div>
                      </div>
                    </div>
                  </div>
                </v-col>
              </v-row>

              <v-data-table
                dark
                dense
                class="mt-4 transparent-table"
                :headers="courseSlotHeaders"
                :items="courseSlotsForSelected"
                :items-per-page="20"
                :sort-by.sync="slotSortBy"
                @click:row="openDrilldown"
                :sort-desc.sync="slotSortDesc">
                <template v-slot:item.weekday="{ item }">
                  {{ weekdayName(item.weekday) }}
                </template>
                <template v-slot:item.time="{ item }">
                  {{ formatTime(item.hour, item.minute) }}
                </template>
                <template v-slot:item.utilization="{ item }">
                  <v-chip x-small :color="utilColor(item.utilization)" dark> {{ item.utilization }}% </v-chip>
                </template>
                <template v-slot:item.avgRating="{ item }">
                  {{ item.avgRating || "–" }}
                </template>
              </v-data-table>
            </template>
          </div>
        </v-tab-item>

        <!-- ============ TAB 3: WOCHENTAG (alle Kurse an einem Tag) ============ -->
        <v-tab-item>
          <div class="pa-4">
            <div class="d-flex flex-wrap align-center mb-3" style="gap: 12px">
              <v-select
                v-model="selectedWeekday"
                :items="weekdayOptions"
                item-text="name"
                item-value="id"
                label="Wochentag"
                dark
                hide-details
                dense
                outlined
                style="max-width: 240px" />
              <v-spacer />
              <v-btn small color="primary" @click="exportWeekday">
                <v-icon left small>mdi-download</v-icon>
                Excel
              </v-btn>
            </div>

            <v-data-table
              dark
              dense
              class="transparent-table"
              :headers="weekdayHeaders"
              :items="slotsForWeekday"
              :items-per-page="25"
              sort-by="avgVisited"
              sort-desc
              @click:row="openDrilldown">
              <template v-slot:item.time="{ item }">
                {{ formatTime(item.hour, item.minute) }}
              </template>
              <template v-slot:item.utilization="{ item }">
                <v-chip x-small :color="utilColor(item.utilization)" dark> {{ item.utilization }}% </v-chip>
              </template>
              <template v-slot:item.avgRating="{ item }">
                {{ item.avgRating || "–" }}
              </template>
            </v-data-table>
          </div>
        </v-tab-item>

        <!-- ============ TAB 4: HEATMAP ============ -->
        <v-tab-item>
          <div class="pa-4">
            <div class="d-flex justify-space-between align-center mb-3">
              <v-select v-model="heatmapMetric" :items="heatmapMetricOptions" dark hide-details dense outlined style="max-width: 280px" />
              <v-btn small color="primary" @click="exportHeatmap">
                <v-icon left small>mdi-download</v-icon>
                Excel
              </v-btn>
            </div>
            <div class="chart-card">
              <apexchart type="heatmap" height="420" :options="heatmapOptions" :series="heatmapSeries" />
            </div>
          </div>
        </v-tab-item>

        <!-- ============ TAB 5: INSTRUKTOREN ============ -->
        <v-tab-item>
          <div class="pa-4">
            <div class="d-flex justify-end mb-3">
              <v-btn small color="primary" @click="exportInstructors">
                <v-icon left small>mdi-download</v-icon>
                Excel
              </v-btn>
            </div>
            <v-data-table
              dark
              dense
              class="transparent-table"
              :headers="instructorHeaders"
              :items="data ? data.instructors : []"
              :items-per-page="25"
              sort-by="avgVisited"
              sort-desc>
              <template v-slot:item.utilization="{ item }">
                <v-chip x-small :color="utilColor(item.utilization)" dark> {{ item.utilization }}% </v-chip>
              </template>
              <template v-slot:item.avgRating="{ item }">
                {{ item.avgRating || "–" }}
              </template>
            </v-data-table>
          </div>
        </v-tab-item>

        <!-- ============ TAB 6: ROHDATEN ============ -->
        <v-tab-item>
          <div class="pa-4">
            <div class="d-flex flex-wrap align-center mb-3" style="gap: 12px">
              <v-text-field
                v-model="search"
                label="Suchen (mehrere mit Komma)"
                dark
                hide-details
                dense
                outlined
                append-icon="mdi-magnify"
                style="min-width: 280px" />
              <v-spacer />
              <v-btn small color="primary" @click="exportRaw">
                <v-icon left small>mdi-download</v-icon>
                Excel
              </v-btn>
            </div>
            <v-data-table
              dark
              dense
              class="transparent-table"
              :loading="loading"
              :headers="rawHeaders"
              :items="filteredCourses"
              :items-per-page="15"
              @dblclick:row="rowDblClick">
              <template v-slot:item.visitedCount="{ item }">
                <v-chip x-small :color="utilColor(item.utilization)" dark> {{ item.visitedCount }} / {{ item.capacity }} </v-chip>
              </template>
              <template v-slot:item.avgRating="{ item }">
                {{ item.avgRating || "–" }}
              </template>
              <template v-slot:item.instructorNames="{ item }">
                {{ item.instructorNames.join(", ") }}
              </template>
            </v-data-table>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
    <!-- ============ SLOT DRILLDOWN DIALOG ============ -->
    <v-dialog v-model="drilldownOpen" max-width="1100" dark>
      <v-card class="glass-card" v-if="drilldownSlot">
        <v-card-title class="d-flex align-center">
          <div>
            <div style="font-size: 1.1rem; font-weight: 700">
              {{ drilldownSlot.courseName }}
            </div>
            <div style="font-size: 0.85rem; opacity: 0.7">
              {{ weekdayName(drilldownSlot.weekday) }}
              {{ formatTime(drilldownSlot.hour, drilldownSlot.minute) }} ·
              {{ drilldownSlot.studioName }}
            </div>
          </div>
          <v-spacer />
          <v-btn icon @click="drilldownOpen = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <!-- Mini KPIs -->
          <v-row dense class="mb-2">
            <v-col cols="6" md="3">
              <div class="mini-kpi">
                <div class="mini-label">Durchführungen</div>
                <div class="mini-value">{{ drilldownSlot.executions }}</div>
              </div>
            </v-col>
            <v-col cols="6" md="3">
              <div class="mini-kpi">
                <div class="mini-label">Ø Teilnehmer</div>
                <div class="mini-value">{{ drilldownSlot.avgVisited }}</div>
              </div>
            </v-col>
            <v-col cols="6" md="3">
              <div class="mini-kpi">
                <div class="mini-label">Ø Auslastung</div>
                <div class="mini-value">{{ drilldownSlot.utilization }}%</div>
              </div>
            </v-col>
            <v-col cols="6" md="3">
              <div class="mini-kpi">
                <div class="mini-label">Ø Bewertung</div>
                <div class="mini-value">{{ drilldownSlot.avgRating || "–" }}</div>
              </div>
            </v-col>
          </v-row>

          <!-- Verlauf Chart -->
          <div class="chart-card mb-3">
            <div class="chart-title">Teilnehmer-Verlauf</div>
            <apexchart type="line" height="240" :options="drilldownChartOptions" :series="drilldownChartSeries" />
          </div>

          <!-- Detail Tabelle -->
          <v-data-table
            dark
            dense
            class="transparent-table"
            :headers="drilldownHeaders"
            :items="drilldownExecutions"
            :items-per-page="15"
            sort-by="ts"
            sort-desc
            @dblclick:row="rowDblClick">
            <template v-slot:item.visitedCount="{ item }">
              <v-chip x-small :color="utilColor(item.utilization)" dark> {{ item.visitedCount }} / {{ item.capacity }} </v-chip>
            </template>
            <template v-slot:item.utilization="{ item }"> {{ item.utilization }}% </template>
            <template v-slot:item.avgRating="{ item }">
              {{ item.avgRating || "–" }}
            </template>
            <template v-slot:item.instructorNames="{ item }">
              {{ item.instructorNames.join(", ") }}
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn small color="primary" @click="exportDrilldown">
            <v-icon left small>mdi-download</v-icon>
            Excel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-overlay :value="loading" absolute opacity="0.4">
      <v-progress-circular indeterminate size="64" color="primary" />
    </v-overlay>
  </div>
</template>

<script>
import api from "../../../api"
import * as XLSX from "xlsx"

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"]
const WEEKDAYS_LONG = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"]

export default {
  name: "GfDashboardV2",
  props: {
    studios: { type: Array, required: true },
    datePickerRange: { type: Array, required: true },
  },
  data() {
    return {
      drilldownOpen: false,
      drilldownSlot: null,
      drilldownHeaders: [
        { text: "Datum", value: "date" },
        { text: "Instruktor", value: "instructorNames" },
        { text: "TN / Plätze", value: "visitedCount" },
        { text: "Anmeldungen", value: "signupsCount" },
        { text: "Auslastung", value: "utilization" },
        { text: "Ø Bewertung", value: "avgRating" },
        { text: "Bewertungen", value: "ratingsCount" },
      ],
      loading: false,
      data: null,
      activeTab: 0,
      search: "",
      selectedCourseId: null,
      selectedWeekday: 1,
      heatmapMetric: "avgVisited",
      slotSortBy: "avgVisited",
      slotSortDesc: true,
      weekdayOptions: WEEKDAYS_LONG.map((name, i) => ({ id: i + 1, name })),
      heatmapMetricOptions: [
        { text: "Ø Teilnehmer", value: "avgVisited" },
        { text: "Auslastung %", value: "utilization" },
        { text: "Anzahl Kurse", value: "executions" },
      ],
      courseSlotHeaders: [
        { text: "Wochentag", value: "weekday" },
        { text: "Zeit", value: "time" },
        { text: "Studio", value: "studioName" },
        { text: "Durchführungen", value: "executions" },
        { text: "Ø TN", value: "avgVisited" },
        { text: "Ø Anmeldungen", value: "avgSignups" },
        { text: "Auslastung", value: "utilization" },
        { text: "Ø Bewertung", value: "avgRating" },
      ],
      weekdayHeaders: [
        { text: "Zeit", value: "time" },
        { text: "Kurs", value: "courseName" },
        { text: "Studio", value: "studioName" },
        { text: "Durchführungen", value: "executions" },
        { text: "Ø TN", value: "avgVisited" },
        { text: "Auslastung", value: "utilization" },
        { text: "Ø Bewertung", value: "avgRating" },
      ],
      instructorHeaders: [
        { text: "Instruktor", value: "name" },
        { text: "Kurse", value: "courseCount" },
        { text: "Ø TN", value: "avgVisited" },
        { text: "Auslastung", value: "utilization" },
        { text: "Ø Bewertung", value: "avgRating" },
      ],
      rawHeaders: [
        { text: "Datum", value: "date" },
        { text: "Studio", value: "studioName" },
        { text: "Kurs", value: "courseName" },
        { text: "Instruktor", value: "instructorNames" },
        { text: "Dauer", value: "duration" },
        { text: "TN / Plätze", value: "visitedCount" },
        { text: "Anmeldungen", value: "signupsCount" },
        { text: "Ø Bewertung", value: "avgRating" },
      ],
    }
  },
  async created() {
    await this.loadData()
  },
  watch: {
    studios() {
      this.loadData()
    },
    datePickerRange() {
      if (this.datePickerRange[0] && this.datePickerRange[1]) this.loadData()
    },
  },
  computed: {
    drilldownExecutions() {
      if (!this.drilldownSlot || !this.data) return []
      const slot = this.drilldownSlot
      return this.data.courses
        .filter((c) => {
          return (
            String(c.courseId) === String(slot.courseId) &&
            String(c.studio) === String(slot.studioId) &&
            c.weekday === slot.weekday &&
            (c.hours || 0) === slot.hour &&
            (c.minutes || 0) === slot.minute
          )
        })
        .sort((a, b) => b.ts - a.ts)
    },
    drilldownChartSeries() {
      const execs = [...this.drilldownExecutions].sort((a, b) => a.ts - b.ts)
      return [
        {
          name: "Anwesend",
          data: execs.map((e) => ({ x: e.ts * 1000, y: e.visitedCount })),
        },
        {
          name: "Anmeldungen",
          data: execs.map((e) => ({ x: e.ts * 1000, y: e.signupsCount })),
        },
        {
          name: "Plätze",
          data: execs.map((e) => ({ x: e.ts * 1000, y: e.capacity })),
        },
      ]
    },
    drilldownChartOptions() {
      return {
        chart: { toolbar: { show: false }, foreColor: "#fff" },
        stroke: { curve: "smooth", width: [3, 2, 2] },
        colors: ["#48c6ef", "#6f86d6", "rgba(255,255,255,0.3)"],
        xaxis: { type: "datetime", labels: { style: { colors: "#fff" } } },
        yaxis: { labels: { style: { colors: "#fff" } } },
        grid: { borderColor: "rgba(255,255,255,0.1)" },
        legend: { labels: { colors: "#fff" } },
        markers: { size: 4 },
        dataLabels: { enabled: false },
      }
    },
    totals() {
      if (!this.data) return { courseCount: 0, totalVisited: 0, avgVisited: 0, utilization: 0, avgRating: 0, ratingCount: 0 }
      const c = this.data.courses
      const totalVisited = c.reduce((a, x) => a + x.visitedCount, 0)
      const totalCap = c.reduce((a, x) => a + x.capacity, 0)
      const ratings = c.filter((x) => x.avgRating !== null)
      const ratingSum = ratings.reduce((a, x) => a + x.avgRating * x.ratingsCount, 0)
      const ratingCount = ratings.reduce((a, x) => a + x.ratingsCount, 0)
      return {
        courseCount: c.length,
        totalVisited,
        avgVisited: c.length ? +(totalVisited / c.length).toFixed(1) : 0,
        utilization: totalCap ? +((totalVisited / totalCap) * 100).toFixed(1) : 0,
        avgRating: ratingCount ? +(ratingSum / ratingCount).toFixed(2) : null,
        ratingCount,
      }
    },
    courseOptions() {
      if (!this.data) return []
      const map = {}
      this.data.courses.forEach((c) => {
        if (c.courseId) map[c.courseId] = c.courseName
      })
      return Object.entries(map)
        .map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name))
    },
    courseSlotsForSelected() {
      if (!this.data || !this.selectedCourseId) return []
      return this.data.slots
        .filter((s) => String(s.courseId) === String(this.selectedCourseId))
        .map((s) => ({ ...s, time: this.formatTime(s.hour, s.minute) }))
    },
    topSlotsForCourse() {
      return [...this.courseSlotsForSelected].sort((a, b) => b.avgVisited - a.avgVisited).slice(0, 5)
    },
    slotsForWeekday() {
      if (!this.data) return []
      return this.data.slots.filter((s) => s.weekday === this.selectedWeekday).map((s) => ({ ...s, time: this.formatTime(s.hour, s.minute) }))
    },
    filteredCourses() {
      if (!this.data) return []
      if (!this.search) return this.data.courses
      const terms = this.search
        .toLowerCase()
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean)
      return this.data.courses.filter((c) => {
        const haystack = `${c.courseName} ${c.studioName} ${c.instructorNames.join(" ")}`.toLowerCase()
        return terms.every((t) => haystack.includes(t))
      })
    },
    // ===== Charts =====
    studioAvgSeries() {
      if (!this.data) return []
      const sorted = [...this.data.studioStats].sort((a, b) => b.avgParticipants - a.avgParticipants)
      return [{ name: "Ø TN", data: sorted.map((s) => s.avgParticipants) }]
    },
    studioAvgOptions() {
      const sorted = this.data ? [...this.data.studioStats].sort((a, b) => b.avgParticipants - a.avgParticipants) : []
      return this.barOpts(
        sorted.map((s) => s.name),
        "#48c6ef",
      )
    },
    studioUtilSeries() {
      if (!this.data) return []
      const sorted = [...this.data.studioStats].sort((a, b) => b.utilization - a.utilization)
      return [{ name: "Auslastung %", data: sorted.map((s) => s.utilization) }]
    },
    studioUtilOptions() {
      const sorted = this.data ? [...this.data.studioStats].sort((a, b) => b.utilization - a.utilization) : []
      return this.barOpts(
        sorted.map((s) => s.name),
        "#50a7c2",
      )
    },
    activeMemberSeries() {
      if (!this.data) return []
      const sorted = [...this.data.studioStats].sort((a, b) => b.activeMemberPercentage - a.activeMemberPercentage)
      return [{ name: "% aktive Mitglieder", data: sorted.map((s) => s.activeMemberPercentage) }]
    },
    activeMemberOptions() {
      const sorted = this.data ? [...this.data.studioStats].sort((a, b) => b.activeMemberPercentage - a.activeMemberPercentage) : []
      return this.barOpts(
        sorted.map((s) => s.name),
        "#6f86d6",
      )
    },
    trendSeries() {
      if (!this.data) return []
      const map = {}
      this.data.courses.forEach((c) => {
        const day = this.$moment(c.ts * 1000).format("YYYY-MM-DD")
        map[day] = (map[day] || 0) + 1
      })
      const sorted = Object.keys(map).sort()
      return [{ name: "Kurse", data: sorted.map((d) => ({ x: d, y: map[d] })) }]
    },
    trendOptions() {
      return {
        chart: { toolbar: { show: false }, foreColor: "#fff" },
        stroke: { curve: "smooth", width: 2 },
        xaxis: { type: "datetime" },
        colors: ["#48c6ef"],
        fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.5, opacityTo: 0 } },
        dataLabels: { enabled: false },
        grid: { borderColor: "rgba(255,255,255,0.1)" },
      }
    },
    courseSlotSeries() {
      return [
        {
          name: "Ø TN",
          data: this.courseSlotsForSelected.map((s) => ({
            x: `${this.weekdayName(s.weekday)} ${this.formatTime(s.hour, s.minute)} – ${s.studioName}`,
            y: s.avgVisited,
          })),
        },
      ]
    },
    courseSlotOptions() {
      return {
        chart: { toolbar: { show: false }, foreColor: "#fff" },
        plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
        colors: ["#48c6ef"],
        dataLabels: { enabled: true, style: { colors: ["#fff"] } },
        grid: { borderColor: "rgba(255,255,255,0.1)" },
        xaxis: { labels: { style: { colors: "#fff" } } },
        yaxis: { labels: { style: { colors: "#fff" } } },
      }
    },
    heatmapSeries() {
      if (!this.data) return []
      // y = weekday, x = hour
      const grid = {}
      for (let w = 1; w <= 7; w++) grid[w] = {}
      this.data.slots.forEach((s) => {
        const cell = grid[s.weekday][s.hour] || { sum: 0, weight: 0, executions: 0 }
        if (this.heatmapMetric === "executions") {
          cell.sum += s.executions
        } else if (this.heatmapMetric === "utilization") {
          cell.sum += s.utilization * s.executions
          cell.weight += s.executions
        } else {
          cell.sum += s.avgVisited * s.executions
          cell.weight += s.executions
        }
        cell.executions += s.executions
        grid[s.weekday][s.hour] = cell
      })
      const series = []
      for (let w = 7; w >= 1; w--) {
        const row = { name: WEEKDAYS_LONG[w - 1], data: [] }
        for (let h = 6; h <= 22; h++) {
          const cell = grid[w][h]
          let val = 0
          if (cell) {
            if (this.heatmapMetric === "executions") val = cell.sum
            else val = cell.weight ? +(cell.sum / cell.weight).toFixed(1) : 0
          }
          row.data.push({ x: `${h}:00`, y: val })
        }
        series.push(row)
      }
      return series
    },
    heatmapOptions() {
      return {
        chart: { toolbar: { show: false }, foreColor: "#fff" },
        dataLabels: { enabled: true, style: { colors: ["#fff"], fontSize: "10px" } },
        colors: ["#48c6ef"],
        xaxis: { labels: { style: { colors: "#fff" } } },
        plotOptions: {
          heatmap: {
            radius: 4,
            useFillColorAsStroke: false,
            colorScale: {
              ranges: [
                { from: 0, to: 0, color: "#1a1a2e", name: "Keine" },
                { from: 0.01, to: 5, color: "#2d4a6b", name: "Gering" },
                { from: 5.01, to: 10, color: "#4a7ba6", name: "Mittel" },
                { from: 10.01, to: 20, color: "#48c6ef", name: "Gut" },
                { from: 20.01, to: 1000, color: "#50fa7b", name: "Sehr gut" },
              ],
            },
          },
        },
      }
    },
  },
  methods: {
    openDrilldown(slot) {
      this.drilldownSlot = slot
      this.drilldownOpen = true
    },
    exportDrilldown() {
      if (!this.drilldownSlot) return
      const slot = this.drilldownSlot
      const rows = this.drilldownExecutions.map((c) => ({
        Datum: c.date,
        Instruktor: c.instructorNames.join(", "),
        Anwesend: c.visitedCount,
        Plätze: c.capacity,
        Anmeldungen: c.signupsCount,
        "Auslastung %": c.utilization,
        "Ø Bewertung": c.avgRating,
        Bewertungen: c.ratingsCount,
      }))
      const fname = `${slot.courseName}_${this.weekdayName(slot.weekday)}_${this.formatTime(slot.hour, slot.minute)}`.replace(/[^a-zA-Z0-9_]/g, "_")
      this.downloadXlsx(rows, slot.courseName.substring(0, 30), `GF_${fname}.xlsx`)
    },
    async loadData() {
      this.loading = true
      try {
        this.data = await api.fetchGroupfitnessDashboardInfosV2({
          studios: this.studios.map((s) => s._id),
          range: this.datePickerRange,
        })
      } catch (e) {
        this.$toast && this.$toast.error("Fehler beim Laden")
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    weekdayName(w) {
      return WEEKDAYS[w - 1] || "?"
    },
    formatTime(h, m) {
      const hh = String(h || 0).padStart(2, "0")
      const mm = String(m || 0).padStart(2, "0")
      return `${hh}:${mm}`
    },
    utilColor(util) {
      if (util >= 75) return "green"
      if (util >= 50) return "light-green darken-1"
      if (util >= 30) return "orange"
      return "red"
    },
    barOpts(categories, color) {
      return {
        chart: { toolbar: { show: false }, foreColor: "#fff" },
        plotOptions: { bar: { borderRadius: 4, columnWidth: "60%" } },
        colors: [color],
        dataLabels: { enabled: false },
        xaxis: {
          categories,
          labels: { style: { colors: "#fff" }, rotate: -35 },
        },
        yaxis: { labels: { style: { colors: "#fff" } } },
        grid: { borderColor: "rgba(255,255,255,0.1)" },
      }
    },
    rowDblClick(_, ctx) {
      //  this.$router.push("/kurse/" + ctx.item._id)
    },
    // ===== Excel Exports (clientseitig via SheetJS) =====
    downloadXlsx(rows, sheetName, filename) {
      const ws = XLSX.utils.json_to_sheet(rows)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, sheetName.substring(0, 31))
      XLSX.writeFile(wb, filename)
    },
    exportOverview() {
      if (!this.data) return
      const rows = this.data.studioStats.map((s) => ({
        Studio: s.name,
        Mitglieder: s.memberCount,
        Kurse: s.courseCount,
        "Anmeldungen total": s.totalSignups,
        "Anwesende total": s.totalVisited,
        "Ø TN/Kurs": s.avgParticipants,
        "Auslastung %": s.utilization,
        "Ø Bewertung": s.avgRating,
        "Unique Besucher": s.uniqueVisitors,
        "% aktive Mitglieder": s.activeMemberPercentage,
      }))
      this.downloadXlsx(rows, "Übersicht", "GF_Übersicht.xlsx")
    },
    exportCourseSlots() {
      const rows = this.courseSlotsForSelected.map((s) => ({
        Kurs: s.courseName,
        Wochentag: this.weekdayName(s.weekday),
        Zeit: this.formatTime(s.hour, s.minute),
        Studio: s.studioName,
        Durchführungen: s.executions,
        "Ø TN": s.avgVisited,
        "Ø Anmeldungen": s.avgSignups,
        "Auslastung %": s.utilization,
        "Ø Bewertung": s.avgRating,
      }))
      const courseName = this.courseOptions.find((c) => c.id === this.selectedCourseId)?.name || "Kurs"
      this.downloadXlsx(rows, courseName, `GF_${courseName}_Slots.xlsx`)
    },
    exportWeekday() {
      const rows = this.slotsForWeekday.map((s) => ({
        Zeit: this.formatTime(s.hour, s.minute),
        Kurs: s.courseName,
        Studio: s.studioName,
        Durchführungen: s.executions,
        "Ø TN": s.avgVisited,
        "Auslastung %": s.utilization,
        "Ø Bewertung": s.avgRating,
      }))
      this.downloadXlsx(rows, WEEKDAYS_LONG[this.selectedWeekday - 1], `GF_${WEEKDAYS_LONG[this.selectedWeekday - 1]}.xlsx`)
    },
    exportHeatmap() {
      if (!this.data) return
      const rows = []
      this.data.slots.forEach((s) => {
        rows.push({
          Wochentag: this.weekdayName(s.weekday),
          Zeit: this.formatTime(s.hour, s.minute),
          Kurs: s.courseName,
          Studio: s.studioName,
          Durchführungen: s.executions,
          "Ø TN": s.avgVisited,
          "Auslastung %": s.utilization,
        })
      })
      this.downloadXlsx(rows, "Heatmap", "GF_Heatmap.xlsx")
    },
    exportInstructors() {
      if (!this.data) return
      const rows = this.data.instructors.map((i) => ({
        Instruktor: i.name,
        Kurse: i.courseCount,
        "Ø TN": i.avgVisited,
        "Auslastung %": i.utilization,
        "Ø Bewertung": i.avgRating,
      }))
      this.downloadXlsx(rows, "Instruktoren", "GF_Instruktoren.xlsx")
    },
    exportRaw() {
      const rows = this.filteredCourses.map((c) => ({
        Datum: c.date,
        Studio: c.studioName,
        Kurs: c.courseName,
        Instruktor: c.instructorNames.join(", "),
        Dauer: c.duration,
        Anwesend: c.visitedCount,
        Plätze: c.capacity,
        Anmeldungen: c.signupsCount,
        "Auslastung %": c.utilization,
        "Ø Bewertung": c.avgRating,
      }))
      this.downloadXlsx(rows, "Kurse", "GF_Kurse.xlsx")
    },
  },
}
</script>

<style lang="scss" scoped>
.gf-dashboard {
  position: relative;
}

.kpi-card {
  border-radius: 16px;
  padding: 20px;
  color: #fff;
  height: 100%;
  backdrop-filter: blur(12px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }

  .kpi-label {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    opacity: 0.85;
  }
  .kpi-value {
    font-size: 2rem;
    font-weight: 700;
    margin-top: 4px;
    line-height: 1;
  }
  .kpi-sub {
    font-size: 0.75rem;
    opacity: 0.8;
    margin-top: 6px;
  }
}

.kpi-1 {
  background: linear-gradient(135deg, #b7f8db 0%, #50a7c2 100%);
}
.kpi-2 {
  background: linear-gradient(135deg, #48c6ef 0%, #6f86d6 100%);
}
.kpi-3 {
  background: linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%);
  color: #1a1a2e;
}
.kpi-4 {
  background: linear-gradient(135deg, #667db6 0%, #0082c8 100%);
}

.glass-card {
  background: rgba(255, 255, 255, 0.04) !important;
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px !important;
}

.chart-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 16px;
  height: 100%;

  .chart-title {
    color: #fff;
    font-weight: 600;
    font-size: 0.9rem;
    margin-bottom: 12px;
  }
}

.transparent-table {
  background: transparent !important;
  ::v-deep .v-data-table__wrapper {
    background: transparent;
  }
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.95rem;
}

.top-slot-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  color: #fff;

  &:last-child {
    border-bottom: none;
  }

  .rank {
    font-size: 1.25rem;
    font-weight: 700;
    opacity: 0.6;
    min-width: 32px;
  }
  .slot-title {
    font-weight: 600;
    font-size: 0.9rem;
  }
  .slot-sub {
    font-size: 0.75rem;
    opacity: 0.65;
  }
  .slot-value {
    font-size: 1.25rem;
    font-weight: 700;
    color: #48c6ef;
  }
}

.clickable-rows {
  ::v-deep tbody tr {
    cursor: pointer;
    transition: background 0.15s ease;
  }
  ::v-deep tbody tr:hover {
    background: rgba(72, 198, 239, 0.08) !important;
  }
}

.top-slot-row.clickable {
  cursor: pointer;
  transition: background 0.15s ease;
  border-radius: 6px;
  padding-left: 8px;
  padding-right: 8px;

  &:hover {
    background: rgba(72, 198, 239, 0.08);
  }
}

.mini-kpi {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 12px;
  text-align: center;

  .mini-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    opacity: 0.7;
    color: #fff;
  }
  .mini-value {
    font-size: 1.4rem;
    font-weight: 700;
    color: #48c6ef;
    margin-top: 4px;
  }
}
</style>
